import React, { useCallback, useEffect, useMemo } from 'react'
import {
  GridContainer,
  GridItem,
  DescriptionTitle,
  ProcessId,
  ElementItem,
  Divider,
  CheckBox,
  ButtonContainer,
  SearchButton,
  SearchIcon,
  ErrorContainer,
  TableCheckBox,
  DataTableContainer,
  ErrorMessageContainer,
  AlertIcon,
  Card,
  NotFoundImg,
  Tooltip,
  TooltipIcon,
  TooltipText
} from './ManageParticipants.styles'
import { useParams } from 'react-router-dom'
import {
  Typography,
  DataTable,
  useViewport,
  useEffectOnce,
  Pagination,
  useInput,
  useCheckBox,
  Select,
  useSelect,
  usePagination,
  checkInputErrorRequired,
  checkInputErrorMaxLength,
  checkInputErrorRegEx,
  useDatePicker,
  DatePicker,
  formatDate
} from '@chilecompra/react-kit'
import { useSelector, useDispatch, batch } from 'react-redux'
import { getLCode } from '../../modules/utils/formatters'
import { onGetParticipantsListThunk } from '../ListParticipants/ListParticipants.actions'
import { Input, Button, Toast } from '@chilecompra/react-kit/components'
import {
  onGetParticipantsListProcessThunk,
  onGetFunctionByProcessThunk,
  onGetParticipantsListPaginateThunk,
  onGetDniDataThunk,
  onSetSelectedItemSuccess,
  onSetResetFormSuccess,
  onSetResetPartFormSuccess,
  onSetNewPersonSuccess,
  onSetFunctionIdSuccess,
  onSetProcessIdSuccess,
  onSetResetDNIErrorApiSuccess,
  onSetResetSelects,
  onSetResetSelectedPerson,
  onSetAddEndDateSuccess,
  onSetAddStartDateSuccess
} from './ManageParticipants.actions'
import {
  INITIAL_PAGE,
  RECORDS_PER_PAGE,
  WRITE_ALLOWED_ROLES,
  FALLBACK_TEXT_TABLE_MESSAGE,
  PATTERN_DATE_OUT,
  PATTERN_TIME_START_OUT,
  PATTERN_TIME_END_OUT,
  TENDERS_MAPPER
} from '../../config/settings/constants'
import { EMAIL_CHECK } from '../../config/settings/regexes'
import {
  checkInputNationalDniError,
  checkInputMaxDateTyping,
  checkInputMinDateTyping
} from '../../modules/utils/checkers'
import { ONLY_LETTERS } from '../../modules/utils/regexes'
import { onSetModalShowSuccess } from '../AddParticipantModal/AddParticipantModal.actions'
import { PUBLIC_URL, REACT_APP_FEATURE_FLAG_ENABLE_CIVIL_REGISTRY } from '../../config/settings/environment'
import ManageParticipantsLoading from './ManageParticipants.loading'
import ManageParticipantsNotAllowed from './ManageParticipants.notAllowed'
import { formatDNI } from '@chilecompra/react-kit/utils'
import ErrorParticipantModalContainer from '../ErrorParticipantModal/ErrorParticipantModal.container'
import ProtectByOrgCode from '../../components/Security/ProtectByOrgCode'

/**
 * The Manage Participant Container.
 */
const ManageParticipantsContainer = () => {
  const { processId, fromWhere } = useParams()
  const dispatch = useDispatch()
  const {
    loading: loadingParticipantList,
    externalCode,
    participantId,
    error: errorParticipantList,
    errorOrgCodeValidation
  } = useSelector(state => state.participantsList)
  const {
    loading,
    loadingDni,
    loadingProcess,
    loadingFunctionByProcess,
    process,
    dniServiceError,
    functions,
    allParticipants,
    error,
    totalPage,
    total,
    itemSelected,
    selectedList,
    dniErrors,
    dniFullName,
    dniRole,
    email,
    dni,
    startDate,
    endDate,
    processMechanismDic
  } = useSelector(state => state.manageParticipants)
  const {
    authInfo: { roles }
  } = useSelector(state => state.authorizationProvider)
  const { onChangePage, page, records, setPage } = usePagination({
    data: allParticipants,
    initialPage: INITIAL_PAGE,
    initialRowsPerPage: RECORDS_PER_PAGE
  })

  const isEnableCivilRegistry =
    REACT_APP_FEATURE_FLAG_ENABLE_CIVIL_REGISTRY === true || REACT_APP_FEATURE_FLAG_ENABLE_CIVIL_REGISTRY === 'true'

  const {
    size: { isMobile, isDownToMedium, isDesktop }
  } = useViewport()

  const initialProcessStage = useMemo(() => {
    const lCode = getLCode(processId)
    if (TENDERS_MAPPER.has(lCode)) {
      return lCode === 'L1' ? 2 : 6
    }

    return processMechanismDic[fromWhere] ?? ''
  }, [fromWhere, processId, processMechanismDic])

  const onSuccessCallBack = participantId => {
    dispatch(
      onGetParticipantsListPaginateThunk({
        page: `${page - 1}`,
        size: RECORDS_PER_PAGE,
        participanteId: participantId
      })
    )
  }

  useEffectOnce(() => {
    batch(() => {
      const payload = {
        processNumber: processId,
        fromWhere,
        onSuccessCallBack
      }
      dispatch(onGetParticipantsListThunk(payload))
      dispatch(onGetParticipantsListProcessThunk())
      dispatch(onSetResetSelectedPerson())
    })
  })

  const handleChangePage = (event, page) => {
    onChangePage(event, page)
    const payload = {
      page: page > 0 ? page - 1 : `${page}`,
      size: RECORDS_PER_PAGE,
      participanteId: participantId,
      loading: false
    }
    if (filterValue) {
      payload.filter = filterValue
    }
    dispatch(onGetParticipantsListPaginateThunk(payload))
  }

  const resetFields = () => {
    resetEmailValue()
    resetNameValue()
    resetRoleValue()
    resetIsAnOfficial()
    setNameValue('')
    setRoleValue('')
  }

  const resetForm = () => {
    dispatch(onSetResetFormSuccess())
    resetDniValue()
    resetFields()
  }

  const resetPartForm = () => {
    dispatch(onSetResetPartFormSuccess())
    resetFields()
  }

  const { value: filterValue, onChange: onChangeFilterValue } = useInput({
    initialValue: ''
  })

  const { value: checkedForeignParticipants, onChange: onChangeForeignParticipants } = useCheckBox({
    initialValue: false,
    changeCallback: () => {
      resetForm()
    }
  })

  const {
    value: dniValue,
    onChange: onChangeDniValue,
    onReset: resetDniValue,
    error: errorDniValue
  } = useInput({
    initialValue: dni || '',
    errorCallbacks: checkedForeignParticipants
      ? [checkInputErrorRequired('Debes ingresar un RUT')]
      : [checkInputErrorRequired('Debes ingresar un RUT'), checkInputNationalDniError],
    formatCallbacks: !checkedForeignParticipants ? [formatDNI] : [],
    overwriteCallback: () => dni,
    changeCallback: () => {
      dispatch(onSetResetDNIErrorApiSuccess())
      resetFields()
    }
  })

  const {
    value: nameValue,
    onChange: onChangeNameValue,
    onReset: resetNameValue,
    error: errorNameValue,
    setValue: setNameValue
  } = useInput({
    initialValue: dniFullName || '',
    errorCallbacks: [
      checkInputErrorRequired('Debes ingresar el nombre y apellido.'),
      checkInputErrorRegEx(ONLY_LETTERS, 'Este campo solo permite letras'),
      checkInputErrorMaxLength(140, 'El nombre y apellido excede el máximo de 140 carácteres permitidos.')
    ],
    formatCallbacks: [value => value?.toUpperCase()],
    overwriteCallback: () => dniFullName
  })

  const {
    value: roleValue,
    onChange: onChangeRoleValue,
    onReset: resetRoleValue,
    error: errorRoleValue,
    setValue: setRoleValue
  } = useInput({
    initialValue: dniRole || '',
    errorCallbacks: [
      checkInputErrorRequired('Debes ingresar el cargo.'),
      checkInputErrorMaxLength(100, 'El cargo excede el máximo de 100 carácteres permitidos.')
    ],
    overwriteCallback: () => dniRole
  })

  const {
    value: emailValue,
    onChange: onChangeEmailValue,
    onReset: resetEmailValue,
    error: errorEmailValue
  } = useInput({
    initialValue: email || '',
    errorCallbacks: [
      checkInputErrorRequired('Debes ingresar tu dirección de correo.'),
      checkInputErrorRegEx(EMAIL_CHECK, 'Tu dirección de correo parece estar en un formato incorrecto.'),
      checkInputErrorMaxLength(80, 'Tu dirección de correo excede el máximo de 80 carácteres permitidos.')
    ],
    overwriteCallback: () => email
  })

  const {
    value: checkedIsAnOfficial,
    onChange: onChangeIsAnOfficial,
    onReset: resetIsAnOfficial
  } = useCheckBox({
    initialValue: false
  })

  const {
    value: selectedProcessStage,
    onChange: onChangeProcessStage,
    error: errorChangeProcessStage
  } = useSelect({
    initialValue: initialProcessStage || '',
    errorCallbacks: [checkInputErrorRequired('Debes seleccionar un mecanismo de compra')],
    changeCallback: updatedValue => {
      resetSelecteFunction()
      return dispatch(onSetProcessIdSuccess({ processIdSelected: updatedValue }))
    }
  })

  const {
    error: errorDateStart,
    value: dateStartValue,
    onChange: handleDateStartChange,
    onReset: resetDateStartValue
  } = useDatePicker({
    overwriteCallback: () => startDate,
    initialValue: startDate || '',
    errorCallbacks: !endDate
      ? [checkInputErrorRequired('Debes seleccionar una fecha inicio de función')]
      : [
          checkInputErrorRequired('Debes seleccionar una fecha inicio de función'),
          checkInputMaxDateTyping(new Date(endDate))
        ],
    changeCallback: updatedValue =>
      dispatch(
        onSetAddStartDateSuccess({
          startDate: formatDate(new Date(updatedValue), `${PATTERN_DATE_OUT} ${PATTERN_TIME_START_OUT}`).replace(
            ' ',
            'T'
          )
        })
      )
  })

  const {
    error: errorDateEnd,
    value: dateEndValue,
    onChange: handleDateEndChange,
    onReset: resetDateEndValue
  } = useDatePicker({
    overwriteCallback: () => endDate,
    initialValue: endDate || '',
    errorCallbacks: !startDate
      ? [checkInputErrorRequired('Debes seleccionar una fecha fin de función')]
      : [
          checkInputErrorRequired('Debes seleccionar una fecha fin de función'),
          checkInputMinDateTyping(new Date(startDate))
        ],
    changeCallback: updatedValue =>
      dispatch(
        onSetAddEndDateSuccess({
          endDate: formatDate(new Date(updatedValue), `${PATTERN_DATE_OUT} ${PATTERN_TIME_END_OUT}`).replace(' ', 'T')
        })
      )
  })

  useEffect(() => {
    if (selectedProcessStage !== '') {
      dispatch(onGetFunctionByProcessThunk(selectedProcessStage))
    }
    return () => {}
  }, [dispatch, selectedProcessStage])

  const {
    value: selectedFunction,
    onChange: onChangeFunction,
    error: errorChangeFunction,
    onReset: resetSelecteFunction
  } = useSelect({
    initialValue: '',
    errorCallbacks: [checkInputErrorRequired('Debes seleccionar una función')],
    changeCallback: updatedValue => dispatch(onSetFunctionIdSuccess({ functionIdSelected: updatedValue }))
  })

  const handleChangeCheckboxList = (val, position) => {
    const newSelectList = selectedList.map((element, index) => (index === position ? !selectedList[position] : false))
    const payload = { selectedList: newSelectList, itemSelected: newSelectList[position] ? val : '' }
    if (newSelectList[position]) {
      const { fullName, role, email, dni, isForeignPerson, isOfficialOfInstitution } = allParticipants[position]
      payload.selectedFullName = fullName
      payload.selectedRole = role
      payload.selectedEmail = email
      payload.selectedDni = dni
      payload.isForeignPerson = isForeignPerson
      payload.isOfficialOfInstitution = isOfficialOfInstitution
    }
    resetSelecteFunction()
    resetDateStartValue()
    resetDateEndValue()
    batch(() => {
      dispatch(onSetResetSelects())
      dispatch(onSetSelectedItemSuccess(payload))
    })
  }

  const handleSearchDniData = () => {
    resetPartForm()
    dispatch(onGetDniDataThunk(dniValue))
  }

  const handleSearchFilter = () => {
    const payload = {
      page: '0',
      size: RECORDS_PER_PAGE,
      filter: filterValue !== '' ? filterValue : false,
      participanteId: participantId
    }
    setPage(INITIAL_PAGE)
    dispatch(onGetParticipantsListPaginateThunk(payload))
  }

  const handleAddParticipantToLocalList = () => {
    const newPerson = {
      dni: dniValue,
      email: emailValue,
      fullName: nameValue,
      participantDetailId: participantId || true,
      role: roleValue,
      isForeignPerson: checkedForeignParticipants,
      isOfficialOfInstitution: checkedIsAnOfficial
    }
    const selectedValues = {
      selectedDni: dniValue,
      selectedEmail: emailValue,
      selectedFullName: nameValue,
      selectedRole: roleValue,
      isForeignPerson: checkedForeignParticipants,
      isOfficialOfInstitution: checkedIsAnOfficial
    }
    const newParticipantList = [newPerson].concat(allParticipants)
    const payload = {
      allParticipants: newParticipantList,
      selectedList: newParticipantList.map((item, index) => index === 0),
      itemSelected: newPerson.participantDetailId,
      page: '0',
      ...selectedValues
    }
    setPage(INITIAL_PAGE)
    dispatch(onSetNewPersonSuccess(payload))
    resetForm()
  }

  const handleShowAddModal = () => {
    dispatch(onSetModalShowSuccess({ showAddParticipantModal: true }))
  }

  const handleDniErrorField = useCallback(() => {
    if ((dniErrors && dniErrors.length > 0) || errorDniValue) {
      return errorDniValue || dniErrors.map(error => error.descripcion).join('. ')
    }
    return ''
  }, [dniErrors, errorDniValue])

  const canNotSubmit = useCallback(() => {
    const haveSomeThing = dniValue !== '' && nameValue !== '' && emailValue !== '' && roleValue !== ''
    const haveError = errorDniValue !== '' || errorNameValue !== '' || errorEmailValue !== '' || errorRoleValue !== ''
    return !haveSomeThing || haveError
  }, [dniValue, emailValue, nameValue, roleValue])

  const canNotAddParticipant = useCallback(() => {
    const haveSomeThing =
      selectedProcessStage !== '' && selectedFunction !== '' && dateStartValue !== '' && dateEndValue !== ''
    const haveError =
      errorChangeProcessStage !== '' || errorChangeFunction !== '' || errorDateStart !== '' || errorDateEnd !== ''
    return !haveSomeThing || haveError
  }, [selectedProcessStage, selectedFunction, dateStartValue, dateEndValue])

  const disabledSubmit = canNotSubmit()
  const disabledCreateParticiantButton = canNotAddParticipant()
  const dniErrorField = handleDniErrorField()

  const onHandleCheckboxChange = (participantDetailId, index) => () => {
    handleChangeCheckboxList(participantDetailId, index)
  }

  useEffectOnce(() => {
    if (selectedProcessStage !== '') dispatch(onSetProcessIdSuccess({ processIdSelected: selectedProcessStage }))
  }, [])

  const validateWriteAllowed = roles.includes(WRITE_ALLOWED_ROLES.supervisorBuyer)

  return validateWriteAllowed ? (
    <ProtectByOrgCode redirectTo="/noAutorizado" validOrgCode={errorOrgCodeValidation}>
      <GridContainer>
        <ErrorParticipantModalContainer />
        <GridItem xs={12}>
          <GridContainer>
            <GridItem xs={12}>
              <DescriptionTitle>
                Añadir participante al proceso de compra {externalCode && <ProcessId> {externalCode}</ProcessId>}
                {externalCode && (
                  <Tooltip
                    content={
                      <TooltipText>
                        Este número de identificación lo genera automáticamente Mercado Público. Bajo este número se
                        agrupan el mecanismo de compra, las órdenes de compra que se generen y, si corresponde, el
                        contrato asociado.
                      </TooltipText>
                    }
                  >
                    <TooltipIcon />
                  </Tooltip>
                )}
              </DescriptionTitle>
            </GridItem>
            <GridItem xs={12} padding="15px 20px 32px 0">
              <Typography fontSize="14px" color="black1" fontWeight="regular" lineHeight="18px">
                Selecciona o crea un nuevo participante. Luego añade la función que desempeño esta persona <br /> dentro
                de este proceso de compra o contratación.
              </Typography>
            </GridItem>
            {!errorParticipantList && !loadingParticipantList && !loading && !error && (
              <GridItem xs={12}>
                <GridContainer item xs={12}>
                  <GridItem xs={12} md={8} padding={isDownToMedium ? '0 0 20px' : '0 40px 44px 0'}>
                    <GridContainer>
                      <ElementItem xs={12}>
                        <Typography
                          fontSize="18px"
                          color="black1"
                          padding="0 0 20px 0"
                          fontWeight="bold"
                          variant="body2"
                        >
                          1. Seleccionar participante
                        </Typography>
                        <GridItem xs={12} md={6} lg={3} padding="5px 0 0">
                          {allParticipants && allParticipants.length > 0 && (
                            <Input
                              tabIndex="0"
                              info=""
                              label=""
                              placeholder=""
                              value={filterValue}
                              onChange={onChangeFilterValue}
                              name="filter"
                              suffix={
                                <SearchButton
                                  onClick={handleSearchFilter}
                                  color="primary"
                                  aria-label="Buscar participante"
                                >
                                  <SearchIcon />
                                </SearchButton>
                              }
                            />
                          )}
                        </GridItem>
                        <GridItem xs={12}>
                          {allParticipants && allParticipants.length > 0 && (
                            <DataTableContainer>
                              <DataTable
                                customPagination={{ onChangePage: handleChangePage, page }}
                                headers={[
                                  { label: '', value: 'selected' },
                                  { label: 'Participante', value: 'name' },
                                  { label: 'RUT / DNI', value: 'dni' },
                                  { label: 'Cargo', value: 'role' },
                                  { label: 'Correo electrónico', value: 'email' }
                                ]}
                                headersCellsProps={[
                                  { enableSort: false },
                                  { enableSort: false },
                                  { enableSort: false },
                                  { enableSort: false },
                                  { enableSort: false }
                                ]}
                                enableStripped
                                initialRows={isMobile ? 15 : 20}
                                loading={loading}
                                records={allParticipants}
                                recordsCellsProps={[{ padding: 0, margin: 0, alignment: 'center' }]}
                                recordsDecorators={[
                                  ({ fullName, participantDetailId }, index) => (
                                    <TableCheckBox
                                      accessibility={{ label: `Seleccionar ${fullName} del listado` }}
                                      checked={selectedList[index]}
                                      name="item selected"
                                      onChange={onHandleCheckboxChange(participantDetailId, index)}
                                      padding="0"
                                    />
                                  ),
                                  ({ fullName }) => fullName || FALLBACK_TEXT_TABLE_MESSAGE,
                                  ({ dni }) => dni || FALLBACK_TEXT_TABLE_MESSAGE,
                                  ({ role }) => role || FALLBACK_TEXT_TABLE_MESSAGE,
                                  ({ email }) => email || FALLBACK_TEXT_TABLE_MESSAGE
                                ]}
                              />
                            </DataTableContainer>
                          )}
                        </GridItem>
                        {!error && !loading && allParticipants.length > 0 && (
                          <Pagination
                            boundaryCount={isMobile ? 1 : 2}
                            count={totalPage}
                            disableFirstButton={false}
                            disableLastButton={false}
                            disableResults
                            alignment="right"
                            siblingCount={isMobile && -1}
                            onChange={handleChangePage}
                            page={page}
                            records={records}
                            size={isMobile ? 'small' : 'medium'}
                            total={total}
                          />
                        )}
                        {!error && !loading && allParticipants && allParticipants.length === 0 && (
                          <Card className="not__found" color="blank" padding="24px">
                            <GridContainer alignItems="center" direction="column" justifyContent="center">
                              <NotFoundImg
                                alt="Icono de no encontrado"
                                src={`${PUBLIC_URL}/assets/images/nofound.svg`}
                              />

                              <Typography fontWeight="bold" margin="16px 0" variant="h4">
                                Ingresa personas para añadirlas como participantes
                              </Typography>

                              <Typography color="black3" variant="body2">
                                Utiliza el panel de la derecha para ingresar a las personas que <br /> añadirás como
                                participantes del proceso de compra
                              </Typography>
                            </GridContainer>
                          </Card>
                        )}
                        <Divider />
                        <Typography fontSize="18px" color="black1" padding="20px 0 0" fontWeight="bold" variant="body2">
                          2. Asignar función del participante
                        </Typography>

                        <Typography padding="8px 0 20px" variant="body2">
                          Debes asignar la etapa del proceso de compra y la función que desempeñó esta persona. Por
                          ejemplo, podría haber participado en la creación y publicación de la licitación, en la
                          aprobación de la orden de compra o en la gestión del contrato.
                        </Typography>
                        <GridContainer>
                          <GridItem xs={12} md={6} padding={!isDownToMedium ? '0 10px 0 0' : 0}>
                            <Select
                              tabIndex="0"
                              aria-label="Etapa del proceso"
                              disabled={loadingProcess || initialProcessStage !== ''}
                              label="Etapa del proceso"
                              value={selectedProcessStage}
                              options={process || []}
                              placeholder="Selecciona"
                              onChange={onChangeProcessStage}
                              error={errorChangeProcessStage}
                            />
                          </GridItem>
                          <GridItem xs={12} md={6} padding={!isDownToMedium ? '0 0 0 10px' : 0}>
                            <Select
                              tabIndex="0"
                              aria-label="Función"
                              disabled={loadingProcess || loadingFunctionByProcess}
                              label="Función"
                              options={functions || []}
                              placeholder="Selecciona"
                              value={selectedFunction}
                              onChange={onChangeFunction}
                              error={errorChangeFunction}
                            />
                          </GridItem>
                        </GridContainer>
                        <GridContainer>
                          <GridItem xs={12} md={8}>
                            <Typography
                              fontSize="14px"
                              color="black1"
                              padding="0 0 5px"
                              fontWeight="bold"
                              variant="body2"
                            >
                              Fechas de participación en la etapa
                            </Typography>
                            <Typography
                              fontSize="14px"
                              color="black1"
                              padding="0 0 20px"
                              fontWeight="regular"
                              variant="body2"
                            >
                              Indica el período en que esta persona participó o participará en la etapa seleccionada.
                            </Typography>
                          </GridItem>
                        </GridContainer>
                        <GridContainer>
                          <GridItem xs={12} md={6} lg={3} padding={!isDownToMedium ? '0 10px 0 0' : 0}>
                            <DatePicker
                              tabIndex="0"
                              aria-label="Fecha inicio de función"
                              name="dateStartValue"
                              error={errorDateStart}
                              label="Fecha inicio de función"
                              value={dateStartValue}
                              maxDate={dateEndValue}
                              onChange={handleDateStartChange}
                              width="100%"
                            />
                          </GridItem>
                          <GridItem xs={12} md={6} lg={3} padding={!isDownToMedium ? '0 0 0 10px' : 0}>
                            <DatePicker
                              tabIndex="0"
                              aria-label="Fecha fin de función"
                              name="dateEndValue"
                              error={errorDateEnd}
                              label="Fecha fin de función"
                              value={dateEndValue}
                              minDate={dateStartValue}
                              onChange={handleDateEndChange}
                              width="100%"
                            />
                          </GridItem>
                        </GridContainer>
                        <ButtonContainer>
                          <Button
                            onClick={handleShowAddModal}
                            disabled={!itemSelected || disabledCreateParticiantButton || loading}
                            color="primary"
                            variant="contained"
                          >
                            Añadir participante
                          </Button>
                        </ButtonContainer>
                      </ElementItem>
                    </GridContainer>
                  </GridItem>
                  <ElementItem xs={12} md={4} margin={isDownToMedium ? '0' : '0 0 44px'}>
                    <Typography fontSize="18px" color="black1" padding="0 0 8px" fontWeight="bold" variant="body2">
                      Nuevo participante
                    </Typography>
                    {(!isEnableCivilRegistry || dniServiceError) && (
                      <Toast margin="8px 0" severity="warning">
                        Debido a intermitencias en el sistema del Registro Civil, deberás ingresar los datos de forma
                        manual.
                      </Toast>
                    )}
                    {!checkedForeignParticipants && (
                      <Typography fontSize="14px" padding="8px 0 0" fontWeight="regular" variant="body2">
                        Ingresa el RUT y luego haz clic en la lupa para cargar la información asociada a la persona.
                      </Typography>
                    )}
                    <Typography padding="32px 0 0" fontSize="14px" color="black1" fontWeight="bold" variant="h3">
                      {checkedForeignParticipants ? 'Número de identifación nacional' : 'RUT'}
                      {checkedForeignParticipants && (
                        <Tooltip
                          content={
                            <TooltipText>
                              Si la persona no tiene RUT, ingresa su documento de identidad (pasaporte, DNI, SSN, etc.)
                            </TooltipText>
                          }
                        >
                          <TooltipIcon />
                        </Tooltip>
                      )}
                    </Typography>
                    <Input
                      tabIndex="0"
                      info=""
                      placeholder=""
                      value={dniValue}
                      onChange={onChangeDniValue}
                      name="dni"
                      suffix={
                        !checkedForeignParticipants &&
                        isEnableCivilRegistry && (
                          <SearchButton
                            loading={loadingDni}
                            onClick={handleSearchDniData}
                            disabled={dniValue === '' || errorDniValue}
                            color="primary"
                            aria-label="Buscar datos de persona"
                          >
                            <SearchIcon />
                          </SearchButton>
                        )
                      }
                      error={dniErrorField}
                    />
                    <CheckBox
                      tabIndex="0"
                      disabled={loadingDni}
                      accessibility={{ label: 'Participante extranjero sin RUT' }}
                      checked={checkedForeignParticipants}
                      label="Participante extranjero sin RUT"
                      name="isForeign"
                      onChange={onChangeForeignParticipants}
                    />
                    <Input
                      tabIndex="0"
                      disabled={!dniServiceError && !checkedForeignParticipants && isEnableCivilRegistry}
                      info=""
                      label="Nombre completo"
                      name="fullName"
                      placeholder=""
                      value={nameValue}
                      onChange={onChangeNameValue}
                      error={errorNameValue}
                    />
                    <Input
                      tabIndex="0"
                      info=""
                      label="Cargo"
                      placeholder=""
                      value={roleValue}
                      name="role"
                      onChange={onChangeRoleValue}
                      error={errorRoleValue}
                    />
                    <Input
                      tabIndex="0"
                      info=""
                      label="Correo electrónico"
                      placeholder=""
                      name="email"
                      value={emailValue}
                      onChange={onChangeEmailValue}
                      error={errorEmailValue}
                    />
                    <CheckBox
                      accessibility={{ label: 'Esta persona es funcionaria de la institución' }}
                      checked={checkedIsAnOfficial}
                      label="Esta persona es funcionaria de la institución"
                      name="isAnOfficial"
                      onChange={onChangeIsAnOfficial}
                    />
                    <ButtonContainer>
                      <Button
                        disabled={loadingDni || disabledSubmit}
                        color="primary"
                        variant="outlined"
                        onClick={handleAddParticipantToLocalList}
                      >
                        Añadir participante al listado
                      </Button>
                    </ButtonContainer>
                  </ElementItem>
                </GridContainer>
              </GridItem>
            )}
            {loading && !error && <ManageParticipantsLoading />}
            {(error || errorParticipantList) && (
              <ErrorContainer item container xs={12} justifyContent="center" alignContent="center">
                <GridItem container xs={12} sm={9} md={9} lg={7} justifyContent="center" alignContent="center">
                  <ErrorMessageContainer>
                    <AlertIcon alt="Icono de alerta" src={`${PUBLIC_URL}/assets/images/custom_alert_icon.svg`} />
                    <Typography
                      fontSize="16px"
                      lineHeight="21px"
                      color="black3"
                      padding={isDesktop ? '0 30px 0 37px' : '20px'}
                      variant="body2"
                    >
                      No ha sido posible obtener información de la institución. Por favor, te pedimos que lo vuelvas a
                      intentar dentro de unos minutos.
                    </Typography>
                  </ErrorMessageContainer>
                </GridItem>
              </ErrorContainer>
            )}
          </GridContainer>
        </GridItem>
      </GridContainer>
    </ProtectByOrgCode>
  ) : (
    <ManageParticipantsNotAllowed />
  )
}

export default ManageParticipantsContainer
